import React, { useState, useEffect, useRef, useCallback } from 'react';
import { clientService } from '../../../services/clientService';
import { vehicleService } from '../../../services/vehicleService';
import Icon from '../../../components/AppIcon';

const ClientAutocomplete = ({ onClientSelected, onNewClient, initialValue = '' }) => {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientVehicles, setClientVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [activeVehicleId, setActiveVehicleId] = useState(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    };
    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const doSearch = useCallback(async (term) => {
    if (!term?.trim()) {
      setIsLoading(true);
      const recent = await clientService?.getFrequent(8);
      setResults(recent);
      setIsLoading(false);
      setIsOpen(true);
      return;
    }
    setIsLoading(true);
    const found = await clientService?.search(term, 10);
    setResults(found);
    setIsLoading(false);
    setIsOpen(true);
  }, []);

  const handleInputChange = (e) => {
    const val = e?.target?.value;
    setQuery(val);
    setSelectedClient(null);
    setShowVehicleSelector(false);

    if (debounceRef?.current) clearTimeout(debounceRef?.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleFocus = () => {
    if (!selectedClient) {
      doSearch(query);
    }
  };

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setQuery(client?.nombre || '');
    setIsOpen(false);
    setLoadingVehicles(true);
    setShowVehicleSelector(true);

    const vehicles = await vehicleService?.getByClient(client?.id);
    setClientVehicles(vehicles);
    setLoadingVehicles(false);

    // Pass full client data including address fields
    onClientSelected?.(client, vehicles);
  };

  const handleSelectVehicle = (vehicle) => {
    setActiveVehicleId(vehicle?.id);
    onClientSelected?.(selectedClient, clientVehicles, vehicle);
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    setShowVehicleSelector(false);
    onNewClient?.(query);
  };

  const handleClear = () => {
    setQuery('');
    setSelectedClient(null);
    setClientVehicles([]);
    setShowVehicleSelector(false);
    setResults([]);
    setActiveVehicleId(null);
    onNewClient?.('');
  };

  // Build address display string
  const getAddressDisplay = (client) => {
    const parts = [
      client?.direccion,
      client?.ciudad,
      client?.estado,
      client?.codigoPostal || client?.codigo_postal,
    ]?.filter(Boolean);
    return parts?.join(', ');
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <Icon name="Search" size={16} className="text-muted-foreground" />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder="Buscar cliente por nombre, teléfono, RFC o email..."
          className="w-full pl-9 pr-10 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {selectedClient && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        )}
        {isLoading && (
          <div className="absolute inset-y-0 right-3 flex items-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Selected client badge with full info */}
      {selectedClient && (
        <div className="mt-2 p-3 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-start gap-2">
            <Icon name="UserCheck" size={16} className="text-primary mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{selectedClient?.nombre}</p>
              <div className="mt-1 space-y-0.5">
                {selectedClient?.telefono && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Phone" size={11} />
                    {selectedClient?.telefono}
                    {selectedClient?.telefonoAlt ? ` / ${selectedClient?.telefonoAlt}` : ''}
                  </p>
                )}
                {selectedClient?.email && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Mail" size={11} />
                    {selectedClient?.email}
                  </p>
                )}
                {selectedClient?.rfc && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="FileText" size={11} />
                    RFC: {selectedClient?.rfc}
                  </p>
                )}
                {getAddressDisplay(selectedClient) && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="MapPin" size={11} />
                    {getAddressDisplay(selectedClient)}
                  </p>
                )}
              </div>
            </div>
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-medium shrink-0">
              {selectedClient?.type === 'company' ? 'Empresa' : 'Individual'}
            </span>
          </div>
        </div>
      )}

      {/* Dropdown results */}
      {isOpen && results?.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {results?.map((client) => (
            <button
              key={client?.id}
              type="button"
              onClick={() => handleSelectClient(client)}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-muted transition-colors text-left"
            >
              <div className="mt-0.5 p-1.5 rounded-full bg-primary/10 shrink-0">
                <Icon name={client?.type === 'company' ? 'Building2' : 'User'} size={14} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{client?.nombre}</p>
                <p className="text-xs text-muted-foreground">
                  {client?.telefono}
                  {client?.empresa ? ` · ${client?.empresa}` : ''}
                  {client?.rfc ? ` · RFC: ${client?.rfc}` : ''}
                </p>
                {getAddressDisplay(client) && (
                  <p className="text-xs text-muted-foreground/70 truncate">
                    <Icon name="MapPin" size={10} className="inline mr-0.5" />
                    {getAddressDisplay(client)}
                  </p>
                )}
              </div>
            </button>
          ))}
          <div className="border-t border-border">
            <button
              type="button"
              onClick={handleCreateNew}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors text-left text-primary"
            >
              <Icon name="UserPlus" size={16} />
              <span className="text-sm font-medium">Crear nuevo cliente{query ? `: "${query}"` : ''}</span>
            </button>
          </div>
        </div>
      )}

      {isOpen && results?.length === 0 && !isLoading && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg">
          <div className="px-4 py-3 text-sm text-muted-foreground">No se encontraron clientes</div>
          <div className="border-t border-border">
            <button
              type="button"
              onClick={handleCreateNew}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors text-left text-primary"
            >
              <Icon name="UserPlus" size={16} />
              <span className="text-sm font-medium">Crear nuevo cliente{query ? `: "${query}"` : ''}</span>
            </button>
          </div>
        </div>
      )}

      {/* Vehicle selector for existing client */}
      {showVehicleSelector && selectedClient && (
        <div className="mt-3 p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="Car" size={16} className="text-primary" />
              Vehículos de {selectedClient?.nombre}
            </h4>
            <span className="text-xs text-muted-foreground">{clientVehicles?.length} unidad(es)</span>
          </div>

          {loadingVehicles ? (
            <div className="flex items-center gap-2 py-2">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-muted-foreground">Cargando vehículos...</span>
            </div>
          ) : clientVehicles?.length === 0 ? (
            <div className="py-2">
              <p className="text-xs text-muted-foreground mb-2">No hay vehículos registrados para este cliente.</p>
              <button
                type="button"
                onClick={() => onClientSelected?.(selectedClient, [], 'new')}
                className="flex items-center gap-2 text-xs text-primary hover:underline"
              >
                <Icon name="PlusCircle" size={13} />
                Agregar primer vehículo
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {clientVehicles?.map((vehicle) => (
                <button
                  key={vehicle?.id}
                  type="button"
                  onClick={() => handleSelectVehicle(vehicle)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-colors text-left ${
                    activeVehicleId === vehicle?.id
                      ? 'bg-blue-50/60 border-l-4 border-l-blue-500 shadow-sm border-blue-200'
                      : 'border-border bg-background hover:bg-primary/5 hover:border-primary/30'
                  }`}
                >
                  <Icon name="Car" size={16} className="text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {vehicle?.marca} {vehicle?.modelo} {vehicle?.año || vehicle?.year}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {vehicle?.placas ? `Placas: ${vehicle?.placas}` : ''}
                      {vehicle?.vin ? ` · VIN: ${vehicle?.vin}` : ''}
                      {vehicle?.color ? ` · ${vehicle?.color}` : ''}
                    </p>
                  </div>
                  <Icon name="ChevronRight" size={14} className="text-muted-foreground shrink-0" />
                </button>
              ))}
              {/* Add new vehicle option */}
              <button
                type="button"
                onClick={() => onClientSelected?.(selectedClient, clientVehicles, 'new')}
                className="w-full flex items-center gap-2 p-2.5 rounded-lg border border-dashed border-primary/40 text-primary hover:bg-primary/5 transition-colors text-left"
              >
                <Icon name="PlusCircle" size={15} />
                <span className="text-xs font-medium">Agregar vehículo nuevo</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientAutocomplete;
